/**
 * FileField.jsx
 * One file input on the Video screen, as Replicate's playground has it: a web
 * address, or a file pasted, dropped or chosen — and, for pictures, something
 * already in the library.
 *
 * A picture from this device is added to the library first, so the video can
 * refer to it by id and it is there to use again. Audio is not something the
 * library can post, so it is kept aside for the video only.
 *
 * Dependencies: services/api.js, LibraryPick.jsx, utils/video.js
 */

import { useRef, useState } from 'react';
import { api } from '../../services/api.js';
import LibraryPick from './LibraryPick.jsx';
import { Image as ImageIcon, Upload, Warning } from '../Icons.jsx';
import { audioType, formatSeconds, measureAudio } from '../../utils/video.js';

const PICTURE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function FileField({ kind, brandId, value, onChange, accept = [] }) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [picking, setPicking] = useState(false);
  const [dropping, setDropping] = useState(false);
  const input = useRef(null);
  const picture = kind === 'picture';

  async function take(file) {
    if (!file) return;
    setError(null);

    const type = picture ? file.type : audioType(file);
    if (picture && !PICTURE_TYPES.includes(type)) {
      setError('Choose a JPEG, PNG or WebP picture.');
      return;
    }
    if (!picture && !accept.includes(type)) {
      setError('Choose an MP3, WAV or FLAC file.');
      return;
    }

    setBusy(true);
    try {
      if (picture) {
        const { media } = await api.media.upload(brandId, file);
        onChange({ media_id: media.id, name: media.original_name ?? file.name, preview: api.media.rawUrl(media.id) });
      } else {
        const seconds = await measureAudio(file);
        const saved = await api.videos.uploadAudio(brandId, file, type, seconds);
        onChange({ key: saved.key, name: saved.name, seconds: saved.seconds, preview: URL.createObjectURL(file) });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function acceptUrl() {
    const text = url.trim();
    if (!text) return;
    if (!/^https:\/\//i.test(text)) {
      setError('The address must start with https://.');
      return;
    }
    setError(null);
    const name = text.split('?')[0].split('/').pop() || text;
    onChange(picture ? { url: text, name, preview: text } : { url: text, name, seconds: null, preview: text });
    setUrl('');
  }

  const onPaste = (e) => {
    const file = e.clipboardData?.files?.[0];
    if (file) {
      e.preventDefault();
      take(file);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDropping(false);
    take(e.dataTransfer?.files?.[0]);
  };

  if (value) {
    const src = value.preview ?? (value.media_id ? api.media.rawUrl(value.media_id) : value.url);
    return (
      <div className="vid-file vid-file--chosen">
        <div className="vid-file__bar">
          <span className="vid-file__name">
            {value.name ?? 'Chosen'}
            {value.seconds ? ` · ${formatSeconds(value.seconds)}` : ''}
          </span>
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => onChange(null)}>
            Clear file
          </button>
        </div>
        {picture && src && <img className="vid-file__preview" src={src} alt="" />}
        {!picture && src && <audio className="vid-file__audio" controls src={src} />}
      </div>
    );
  }

  return (
    <div
      className={`vid-file${dropping ? ' is-dropping' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDropping(true);
      }}
      onDragLeave={() => setDropping(false)}
      onDrop={onDrop}
    >
      <div className="vid-file__row">
        <input
          type="url"
          value={url}
          placeholder="Enter a URL, paste a file, or drag a file over."
          onChange={(e) => setUrl(e.target.value)}
          onPaste={onPaste}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              acceptUrl();
            }
          }}
          disabled={busy}
        />
        {url.trim() && (
          <button type="button" className="btn btn--sm" onClick={acceptUrl}>
            Use
          </button>
        )}
        {picture && (
          <button
            type="button"
            className="icon-btn"
            title="Choose from the library"
            aria-label="Choose from the library"
            onClick={() => setPicking(true)}
            disabled={busy || !brandId}
          >
            <ImageIcon />
          </button>
        )}
        <button
          type="button"
          className="icon-btn"
          title="Choose a file"
          aria-label="Choose a file"
          onClick={() => input.current?.click()}
          disabled={busy || !brandId}
        >
          <Upload />
        </button>
        <input
          ref={input}
          type="file"
          hidden
          accept={picture ? PICTURE_TYPES.join(',') : [...accept, '.mp3', '.wav', '.flac'].join(',')}
          onChange={(e) => {
            take(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </div>

      {busy && <p className="vid-field__hint">{picture ? 'Adding it to the library…' : 'Uploading…'}</p>}
      {error && (
        <p className="vid-file__error">
          <Warning size={15} /> {error}
        </p>
      )}

      {picking && (
        <LibraryPick
          brandId={brandId}
          onClose={() => setPicking(false)}
          onPick={(m) => {
            setPicking(false);
            onChange({ media_id: m.id, name: m.original_name ?? 'Library picture', preview: api.media.rawUrl(m.id) });
          }}
        />
      )}
    </div>
  );
}
